import ModalViewModel from '../../../infra/ModalViewModel'
import { action, computed, makeObservable, observable } from 'mobx'
import { task } from 'mobx-task'
import { AlertFactory, AlertState } from '../../../alerts/Alert'
import { func, pattern, required, validationContext } from 'validx'
import { SearchableUser } from '../../../iam/models/SearchableUser'
import { UserDataType } from '../../../iam/models/UserDataType'
import MemberStore from '../../../iam/MemberStore'
import { Id } from '../../../types/definitions'
import Workspace from '../../../iam/models/Workspace'
import UserDataStore from '../../../iam/UserDataStore'
import { GetCurrentMember } from '../../../iam/SessionStore'

interface IUserTypeOptions {
  title: string
  value: UserDataType
}

export class EditUserInformationDialogViewModel extends ModalViewModel {
  @observable
  currentUser: SearchableUser = SearchableUser.none

  @observable
  givenName = ''

  @observable
  familyName = ''

  @observable
  email = ''

  @observable
  phone = ''

  @observable
  type: UserDataType = UserDataType.User

  @observable
  workspace: Workspace = Workspace.none

  userTypeOptions: IUserTypeOptions[] = [
    {
      title: 'Admin',
      value: UserDataType.Admin,
    },
    {
      title: 'User',
      value: UserDataType.User,
    },
    {
      title: 'Pro',
      value: UserDataType.Pro,
    },
  ]

  validation = validationContext(this, {
    givenName: [
      required(),
      func(({ value }) => {
        return (
          value.length <= 40 || "First name can't be more than 40 characters"
        )
      }),
    ],
    familyName: [
      required(),
      func(({ value }) => {
        return (
          value.length <= 40 || "Last name can't be more than 40 characters"
        )
      }),
    ],
    email: [
      required(),
      pattern('email'),
      func(({ value }) => {
        return value.length <= 320 || "Email can't be more than 320 characters"
      }),
    ],
    phone: [
      required(),
      func(
        (ctx) =>
          !ctx.value ||
          pattern(
            /^[+]?[ 0-9()-]+$/i,
            'This does not look like a phone number.',
          )(ctx),
      ),
      func(({ value }) => {
        return value.length <= 20 || "Phone can't be more than 20 characters"
      }),
    ],
  })

  constructor(
    private newAlert: AlertFactory,
    private getCurrentMember: GetCurrentMember,
    private memberStore: MemberStore,
    private userDataStore: UserDataStore,
    private getSearchableUserDetails: () => Promise<void>,
  ) {
    super()
    makeObservable(this)
  }

  @action.bound
  open(currentUser: SearchableUser, workspace: Workspace) {
    this.currentUser = currentUser
    this.givenName = currentUser.givenName
    this.familyName = currentUser.familyName
    this.email = currentUser.email
    this.phone = currentUser.phone
    this.type = currentUser.userDataType
    this.workspace = workspace

    this.show()
  }

  @computed
  get selectedUserType() {
    return this.userTypeOptions.find((i) => i.value === this.type)
  }

  @computed
  get isPlatformAdmin() {
    return this.getCurrentMember().user.platformAdmin
  }

  @action.bound
  setUserType(userType?: IUserTypeOptions) {
    if (userType) {
      this.type = userType.value
    }
  }

  submit = task.resolved(async () => {
    const alert = this.newAlert()

    if (!this.validation.reset().validate().isValid) {
      return
    }

    try {
      alert
        .withState(AlertState.Pending)
        .saying('Saving user data!')
        .autoDismiss()
        .show()

      await Promise.all([
        await this.memberStore.update(
          {
            workspace_id: Id.assert(this.workspace.id),
            user_public_id: Id.assert(this.currentUser.publicId),
          },
          {
            family_name: this.familyName,
            given_name: this.givenName,
            phone: this.phone,
            email: this.email,
          },
        ),
        this.isPlatformAdmin &&
          this.userDataStore.update(this.currentUser.publicId, {
            type: this.type,
          }),
      ])

      alert
        .withState(AlertState.Success)
        .saying('Success!', 'User data saved successfully!')
        .autoDismiss()
        .show()

      await this.getSearchableUserDetails()

      this.close()
    } catch (e) {
      console.error(e)

      alert.fromError(e).autoDismiss()
    }
  })
}
